import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Container,
  Text,
} from "@chakra-ui/react"
import NormalHeading from "@components/headings/NormalHeading"
import HeaderLayout from "@components/layout/HeaderLayout"
import { BlogPageProps, EmenuBlogsTranslation } from "@definitions/types"
import { readItems } from "@directus/sdk"
import { routing } from "@i18n/routing"
import directusClient from "@utils/directus"
import {
  loadMessages,
  normaliseLuxonLocale,
  resolveTranslation,
} from "@utils/utils"
import { DateTime, Settings } from "luxon"
import { GetStaticPaths, GetStaticProps, NextPage } from "next"
import dynamic from "next/dynamic"
import Image from "next/image"
import NextLink from "next/link"
import { useTranslations } from "next-intl"
import { MDXRemote } from "next-mdx-remote"
import { serialize } from "next-mdx-remote/serialize"
import { ArticleJsonLd, NextSeo } from "next-seo"
import { FiChevronRight } from "react-icons/fi"

const DynamicFooter = dynamic(() => import("@components/layout/Footer"), {
  ssr: false,
})

const BlogPost: NextPage<BlogPageProps> = ({ blog, locale, locales }) => {
  Settings.defaultLocale = routing.defaultLocale
  Settings.defaultZone = "Asia/Dubai"

  const luxonLocale = normaliseLuxonLocale(locale)
  const t = useTranslations("blog")

  return (
    <>
      <NextSeo
        title={blog.meta_title}
        canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/blog/${blog.slug}`}
        languageAlternates={locales.map((lang: string) => {
          const prefix = lang === routing.defaultLocale ? "" : `/${lang}`
          return {
            hrefLang: lang,
            href: `${process.env.NEXT_PUBLIC_SITE_URL}${prefix}/blog/${blog.slug}`,
          }
        })}
        description={blog.meta_description}
        openGraph={{
          title: `${blog.meta_title}`,
          description: `${blog.excerpt ?? blog.meta_description}`,
          url: `${process.env.NEXT_PUBLIC_SITE_URL}/blog/${blog.slug}`,
          type: "article",
          article: {
            publishedTime: `${blog.date_created}`,
            modifiedTime: `${blog.date_updated}`,
            authors: [`${blog.author}`],
            tags: blog.tags,
          },
          images: blog?.image?.filename_disk
            ? [
                {
                  url: `${process.env.NEXT_PUBLIC_IMAGE_CDN}${blog.image.filename_disk}?key=blog-large`,
                  width: blog.image.width ?? 1200,
                  height: blog.image.height ?? 800,
                  alt: `${blog.title}`,
                },
              ]
            : [],
        }}
      />
      <ArticleJsonLd
        url={`${process.env.NEXT_PUBLIC_SITE_URL}/blog/${blog.slug}`}
        title={blog.meta_title}
        images={[
          `${process.env.NEXT_PUBLIC_IMAGE_CDN}${blog.image.filename_disk}?key=blog-large`,
        ]}
        keywords={blog.tags}
        datePublished={blog.date_created}
        dateModified={blog.date_updated ?? undefined}
        authorName={[`${blog.author}`]}
        publisherName={"Digital eMenu"}
        publisherLogo={`${process.env.NEXT_PUBLIC_SITE_URL}/images/logo.png`}
        description={blog.excerpt ?? blog.meta_description}
      />
      <HeaderLayout />
      <Container
        maxW={"container.xl"}
        px={10}
      >
        <Box
          roundedBottom={"xl"}
          overflow={"hidden"}
          shadow={"2xl"}
        >
          <Image
            src={`${process.env.NEXT_PUBLIC_IMAGE_CDN}${blog.image.filename_disk}?key=blog-large`}
            loader={({ src }) => src}
            alt={blog.title}
            width={blog.image.width ?? 1200}
            height={blog.image.height ?? 675}
            priority
            quality={90}
            style={{
              width: "100%",
            }}
          />
        </Box>
        <Box
          mt={10}
          fontSize={"xs"}
          color={"white"}
          bg={"brand.primary"}
          w={"max-content"}
          px={2}
          py={1}
          rounded={"full"}
          shadow={"sm"}
        >
          {DateTime.fromISO(blog.date_created)
            .setLocale(luxonLocale)
            .toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}
        </Box>
        <Breadcrumb
          fontSize={"xs"}
          spacing={2}
          separator={<FiChevronRight color={"brand.gray"} />}
          my={4}
        >
          <BreadcrumbItem>
            <NextLink
              href={`${process.env.NEXT_PUBLIC_SITE_URL}/`}
              passHref
              legacyBehavior
            >
              <BreadcrumbLink>{t("home")}</BreadcrumbLink>
            </NextLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <NextLink
              href={`${process.env.NEXT_PUBLIC_SITE_URL}/blog`}
              passHref
              legacyBehavior
            >
              <BreadcrumbLink>{t("blog")}</BreadcrumbLink>
            </NextLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>
              <Text
                noOfLines={1}
                maxW={200}
              >
                {blog.title}
              </Text>
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        <NormalHeading
          as={"h1"}
          px={0}
          mt={10}
        >
          {blog.title}
        </NormalHeading>
        <Box
          color={"brand.black"}
          mt={10}
        >
          <MDXRemote {...blog.content} />
        </Box>
      </Container>
      <DynamicFooter />
    </>
  )
}

export default BlogPost

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const activeLocales = locales?.filter((loc) => loc !== "default") ?? [
    ...routing.locales,
  ]

  const blogs = await directusClient.request(
    readItems("emenu_blogs", {
      limit: -1,
      fields: ["slug"],
      filter: {
        status: {
          _eq: "published",
        },
      },
    })
  )

  const paths = blogs?.map((blog) =>
    activeLocales?.map((loc) => ({
      params: { slug: blog.slug },
      locale: loc,
    }))
  )

  return { paths: paths ? paths.flat() : [], fallback: "blocking" }
}

export const getStaticProps: GetStaticProps = async ({
  params,
  locale,
  locales,
}) => {
  const blog = await directusClient
    .request(
      readItems("emenu_blogs", {
        limit: 1,
        filter: {
          slug: {
            _eq: params?.slug as string,
          },
          status: {
            _eq: "published",
          },
        },
        fields: [
          "slug",
          "tags",
          "author",
          "image.filename_disk",
          "image.title",
          "image.width",
          "image.height",
          { translations: ["*"] },
          "date_created",
          "date_updated",
        ],
      })
    )
    .then((res) => (res.length > 0 ? res[0] : null))
    .catch(() => null)

  if (!blog) return { notFound: true }

  const trans = resolveTranslation(
    blog.translations as EmenuBlogsTranslation[],
    locale
  )

  // If even English is missing, 404.
  if (!trans) return { notFound: true }

  // Merge base + chosen translation (translation fields win)
  const data = { ...blog, ...trans }

  const { content = "", ...rest } = data

  const blog_data = {
    ...rest,
    content: await serialize(content),
  }

  const [shared, blog_t] = await Promise.all([
    loadMessages("shared", locale ?? routing.defaultLocale),
    loadMessages("blog", locale ?? routing.defaultLocale),
  ])

  return {
    props: {
      blog: blog_data,
      locale,
      locales,
      messages: { ...shared, ...blog_t },
    },
  }
}
